import { FC, memo, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import { CardActionArea, Chip, ListItemText, Menu, MenuItem, IconButton } from '@mui/material';
import { MoreHorizOutlined } from '@mui/icons-material';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { RouteMap } from '@/routes/Routes';
import videoPlaceholder from '@/assets/placeholder-video.png';
import audioPlaceholder from '@/assets/placeholder-audio.png';
import { MoreButton, StyledCard, StyledCardContent, StyledTypography } from './styles';
import { SmallerChip } from '../Tag/styles';
import DeleteModal from '../CustomModal/DeleteModal';
import { formatDateForNote } from '@/helpers/formatDate';
import { formatOwnerName } from '@/helpers/ownerName';
import useFetchTranscript from '@/hooks/useFetchTranscript';
import useRealTimeProcessingType from '@/hooks/useRealTimeProcessingType';
import LoadingCard from './LoadingCard';
import { fetchGetNote, useGetNote, useToggleFavorite } from '@/services/noteGoApi/noteGoApiComponents';
import { useAuthStore } from '@/stores/authStore';
import { resolveMediaUrl } from '@/utils/firebaseUtils';
import useAlertStore from '@/stores/alertStore';
import type { SharedBy } from '@/services/noteGoApi/noteGoApiSchemas';
import { logger } from '@/services/loggerService';

import Box from '@mui/material/Box';

interface NoteCardProps {
  title: string;
  noteId: string;
  tags?: string[];
  transcript?: string;
  onConfirmDelete?: (noteId: string) => void;
  onMoveToFolder?: (noteId: string) => void;
  /** Open the team move/remove modal for this note (personal views only). */
  onMoveToTeam?: (noteId: string) => void;
  hasVideo?: boolean;
  createdAt?: string;
  processingType?: string | null | undefined;
  thumbnailUrl?: string;
  creatorUserId?: string;
  sharedBy?: SharedBy | null;
  isFavorite?: boolean;
  onFavoriteToggle?: (noteId: string, newValue: boolean) => void;
  /**
   * Persist the star somewhere other than the note itself.
   *
   * Default (omitted) writes `notes/{id}.is_favorite` via the API, which only
   * the note's owner is allowed to do. Watched Notes are other people's notes,
   * so that view passes a writer that stores the star on the viewer's own user
   * doc instead. The button, its states and `onFavoriteToggle` are unchanged
   * either way — only the destination differs.
   */
  saveFavorite?: (noteId: string, newValue: boolean) => Promise<void>;
  navigateUrl?: string;
  /** Show a "Team" badge — this note is shared into one of the user's teams. */
  isTeamNote?: boolean;
  hideFavorite?: boolean;
  menuActionLabel?: string;
  deleteModalTitle?: string;
  deleteModalText?: string;
  deleteButtonText?: string;
}

const NoteCard: FC<NoteCardProps> = ({
  title,
  noteId,
  tags,
  transcript,
  onConfirmDelete,
  onMoveToFolder,
  onMoveToTeam,
  hasVideo,
  createdAt,
  processingType,
  thumbnailUrl,
  creatorUserId,
  sharedBy: propSharedBy,
  isFavorite,
  onFavoriteToggle,
  saveFavorite,
  navigateUrl,
  isTeamNote,
  hideFavorite,
  menuActionLabel,
  deleteModalTitle,
  deleteModalText,
  deleteButtonText,
}) => {
  const { userToken, userData } = useAuthStore();
  const showAlert = useAlertStore((state) => state.showAlert);
  const { mutateAsync: toggleFavorite } = useToggleFavorite();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [noteData, setNoteData] = useState({
    title,
    tags,
    transcript,
    hasVideo,
    createdAt,
    processingType,
    thumbnailUrl,
  });
  useFetchTranscript(noteData.transcript || null);
  const isProcessing = useRealTimeProcessingType(noteId, noteData.processingType);
  const [thumbnail, setThumbnail] = useState<string>(noteData.hasVideo ? videoPlaceholder : audioPlaceholder);

  // Re-sync the cached note data when the parent passes new values (e.g. after
  // a parent refetch with stable keys). Guarded on actual prop changes so the
  // fresh title fetched by the Untitled refetch below isn't clobbered by a
  // stale prop on unrelated re-renders.
  const lastPropsRef = useRef({ title, tags, transcript, hasVideo, createdAt, processingType, thumbnailUrl });
  useEffect(() => {
    const prev = lastPropsRef.current;
    if (
      prev.title === title &&
      prev.tags === tags &&
      prev.transcript === transcript &&
      prev.hasVideo === hasVideo &&
      prev.createdAt === createdAt &&
      prev.processingType === processingType &&
      prev.thumbnailUrl === thumbnailUrl
    ) {
      return;
    }
    lastPropsRef.current = { title, tags, transcript, hasVideo, createdAt, processingType, thumbnailUrl };
    setNoteData({ title, tags, transcript, hasVideo, createdAt, processingType, thumbnailUrl });
  }, [title, tags, transcript, hasVideo, createdAt, processingType, thumbnailUrl]);

  // Check if this note is shared with the current user (not owned by them)
  const isSharedNote = creatorUserId && userData?.id && creatorUserId !== userData.id;

  // Load detailed shared_by info if needed (search endpoint may not include it).
  // Routed through React Query rather than a bare per-card fetch so the requests
  // dedupe and stay cached across scroll batches and remounts — a raw fetch here
  // meant /shared-with-me issued one uncached XHR per card, every mount.
  const needsSharedBy = !!isSharedNote && !propSharedBy;
  const { data: sharedNoteDetail } = useGetNote(
    { headers: { authorization: `Bearer ${userToken}` }, pathParams: { noteId } },
    { enabled: needsSharedBy && !!userToken },
  );
  // Format shared_by name/email
  const effectiveSharedBy: SharedBy | null | undefined = propSharedBy ?? sharedNoteDetail?.shared_by;
  const sharedByName = formatOwnerName(effectiveSharedBy);

  const getNewNoteData = async () => {
    await fetchGetNote({
      headers: {
        authorization: `Bearer ${userToken}`,
      },
      pathParams: {
        noteId,
      },
    })
      .then((response) => {
        setNoteData({
          title: response.title || title,
          tags: response.tags,
          transcript,
          hasVideo: response.video_url ? true : false,
          createdAt: response.created_at,
          processingType: response.processing_type,
          thumbnailUrl: response.thumbnail_url || '',
        });
      })
      .catch((error) => {
        logger.warn('notes', 'Failed to fetch note data', error);
        return noteData;
      });
  };

  // Pull the AI-generated title once processing finishes. Gate on an *observed*
  // processing -> done transition for this card: keying off `title === 'Untitled'`
  // alone re-fetched every genuinely untitled note on every mount (20 requests
  // per list batch), and refetching those is pointless — the backend has no title
  // for them either. Reset on noteId so a recycled card can't fire for the wrong note.
  const wasProcessingRef = useRef(false);
  useEffect(() => {
    wasProcessingRef.current = false;
  }, [noteId]);

  useEffect(() => {
    if (isProcessing) {
      wasProcessingRef.current = true;
      return;
    }
    if (wasProcessingRef.current) {
      wasProcessingRef.current = false;
      getNewNoteData();
    }
  }, [isProcessing, noteId]);

  const handleNavigateToNote = () => {
    if (navigateUrl) {
      navigate(navigateUrl);
      return;
    }
    navigate(`${RouteMap.NOTE}/${noteId}`);
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleOpenDeleteModal = (event: React.MouseEvent<HTMLLIElement>) => {
    event.stopPropagation();
    setIsModalOpen(true);
    setAnchorEl(null);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleConfirmDelete = () => {
    if (onConfirmDelete) {
      onConfirmDelete(noteId);
    }
    setIsModalOpen(false);
  };

  const handleMoveToFolder = (event: React.MouseEvent<HTMLLIElement>) => {
    event.stopPropagation();
    setAnchorEl(null);
    onMoveToFolder?.(noteId);
  };

  const handleMoveToTeam = (event: React.MouseEvent<HTMLLIElement>) => {
    event.stopPropagation();
    setAnchorEl(null);
    onMoveToTeam?.(noteId);
  };

  const handleToggleFavorite = async (event: React.MouseEvent) => {
    event.stopPropagation();

    try {
      const newValue = !isFavorite;
      if (saveFavorite) {
        await saveFavorite(noteId, newValue);
      } else {
        await toggleFavorite({
          headers: { authorization: `Bearer ${userToken}` },
          pathParams: { noteId },
          body: { is_favorite: newValue },
        });
      }
      onFavoriteToggle?.(noteId, newValue);
    } catch (err) {
      logger.warn('notes', 'Failed to toggle favorite status', err);
      showAlert('Something went wrong while updating favorite status. Try again later.', 'error');
    }
  };

  // Hide the "⋯" button entirely when no menu item would render (mirrors
  // NoteListRow's hasMenuItems guard) — e.g. team view as a non-owner.
  const hasMenuItems = !!onMoveToFolder || !!onMoveToTeam || !!onConfirmDelete;

  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      id="primary-search-account-menu"
      keepMounted
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      open={isMenuOpen}
      onClose={() => setAnchorEl(null)}
      sx={{ marginTop: '8px' }}
    >
      {onMoveToFolder && (
        <MenuItem dense onClick={handleMoveToFolder}>
          <ListItemText>Move to folder</ListItemText>
        </MenuItem>
      )}
      {onMoveToTeam && (
        <MenuItem dense onClick={handleMoveToTeam}>
          <ListItemText>{isTeamNote ? 'Move note' : 'Move to team'}</ListItemText>
        </MenuItem>
      )}
      {onConfirmDelete && (
        <MenuItem dense onClick={handleOpenDeleteModal}>
          <ListItemText>{menuActionLabel || 'Archive'}</ListItemText>
        </MenuItem>
      )}
    </Menu>
  );

  // Fetch thumbnail URL from Firebase Storage
  useEffect(() => {
    let cancelled = false;

    const fetchThumbnail = async () => {
      try {
        if (!noteData.hasVideo) {
          if (!cancelled) setThumbnail(audioPlaceholder);
          return;
        }

        if (!noteData?.thumbnailUrl) {
          if (!cancelled) setThumbnail(videoPlaceholder);
          return;
        }

        const secureUrl = await resolveMediaUrl(noteData.thumbnailUrl);
        if (!cancelled && secureUrl) setThumbnail(secureUrl);
      } catch (err) {
        // Thumbnail fetch failed - use placeholder (this is expected for missing/expired videos)
        logger.debug('notes', 'Failed to fetch thumbnail, using placeholder', err);
        if (!cancelled) setThumbnail(videoPlaceholder);
      }
    };

    fetchThumbnail();

    return () => {
      cancelled = true;
    };
  }, [noteData.hasVideo, noteData.thumbnailUrl]);

  return (
    <>
      {noteData.processingType === null ? (
        <LoadingCard onClick={handleNavigateToNote} />
      ) : (
        <StyledCard isActive={isMenuOpen || isModalOpen}>
          {hasMenuItems && (
            <MoreButton size="small" onClick={handleOpenMenu}>
              <MoreHorizOutlined />
            </MoreButton>
          )}
          {!hideFavorite && (
            <IconButton
              size="small"
              onClick={handleToggleFavorite}
              sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                },
              }}
              aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            >
              {isFavorite ? (
                <StarIcon fontSize="small" sx={{ color: '#FFD700' }} />
              ) : (
                <StarBorderIcon fontSize="small" />
              )}
            </IconButton>
          )}
          <CardActionArea disableRipple onClick={handleNavigateToNote}>
            <Box sx={{ position: 'relative' }}>
              <CardMedia component="img" height="140" image={thumbnail} alt="Note preview" loading="lazy" />
            </Box>
            {isTeamNote && (
              <Chip
                icon={<GroupsOutlinedIcon />}
                label="Team"
                size="small"
                color="primary"
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 1,
                  height: 22,
                  fontWeight: 600,
                  pointerEvents: 'none',
                  '& .MuiChip-icon': { fontSize: 14, ml: '4px' },
                  '& .MuiChip-label': { px: '6px' },
                }}
              />
            )}
            {isSharedNote && sharedByName && (
              <SmallerChip
                label={sharedByName}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 1,
                  backgroundColor: 'primary.dark',
                  color: 'primary.contrastText',
                  pointerEvents: 'none',
                  maxWidth: 'calc(100% - 16px)',
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                }}
              />
            )}
            <StyledCardContent>
              <StyledTypography variant="caption">{noteData.title}</StyledTypography>
              <Typography variant="caption" component="div" color="textSecondary">
                {formatDateForNote(noteData.createdAt || '')}
              </Typography>
            </StyledCardContent>
          </CardActionArea>
          {hasMenuItems && renderMenu}
        </StyledCard>
      )}
      <DeleteModal
        open={isModalOpen}
        handleClose={handleCloseModal}
        title={deleteModalTitle || 'Archive note?'}
        text={deleteModalText || 'Are you sure you want to archive this note?'}
        confirmButtonText={deleteButtonText || 'Yes, archive'}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

export default memo(NoteCard);
